import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import useUser from '../../hooks/useUser';
import useCheckoutRedirect from '../../hooks/useCheckoutRedirect';
import useCourseCatalog from '../../hooks/useCourseCatalog';
import { pushEvent, ANALYTICS_EVENTS } from '../../utils/analytics';
import Reveal from '../../components/course/Reveal';
import StarRating, { TRUSTPILOT_URL } from '../../components/course/StarRating';
import LogoRow from '../../components/course/LogoRow';
import FactStrip from '../../components/course/FactStrip';
import PlanSelector from '../../components/course/PlanSelector';
import PromoCountdown from '../../components/course/PromoCountdown';
import CurriculumAccordion from '../../components/course/CurriculumAccordion';
import PreviewVideo from '../../components/course/PreviewVideo';
import GuaranteeBlock from '../../components/course/GuaranteeBlock';
import ScrollProgress from '../../components/course/ScrollProgress';
import {
  NO_CODING_BADGE, FIRST_NIGHT_PROMISE, GUARANTEE_DAYS, COMPETITION,
} from '../../components/course/courseContent';
import './CoursePage.css';

const FIT_CALL_URL = 'https://calendly.com/lumiwealth/botspot-call';
export const SELF_PACED_PATH = '/courses/ai-trading-bootcamp/self-paced';
export const CHALLENGES_PATH = '/challenges';

const HERO_OUTCOME = 'Place a Paper Trade Every Week. Finish With an Always-On AI Trading Agent.';
const HERO_SUB =
  'Use Claude Code, Codex, BotSpot, TradingView, and your broker to research stocks, scan markets, backtest a strategy, and schedule an AI-native trading agent.';

/** The experiment changes the product name only. */
export const HERO_VARIANTS = {
  control: { key: 'trading', courseName: 'AI Trading Bootcamp' },
  agentic: { key: 'agentic', courseName: 'Agentic Trading Bootcamp' },
};

const PAIN_POINTS = [
  {
    img: '/images/courses/problem-sample-size.webp',
    title: 'Five green days is not proof',
    body: 'A good week does not tell you whether the idea has an edge.',
  },
  {
    img: '/images/courses/problem-close-enough.webp',
    title: 'It said 6785. It was 6780',
    body: 'A confident answer can still be wrong enough to cost you.',
  },
  {
    img: '/images/courses/problem-research-time.webp',
    title: 'Two hours before the bell',
    body: 'Manual scanning consumes the morning before a decision is even made.',
  },
];

const DELIVERABLES = [
  {
    title: 'Your AI trading tools connected',
    detail: 'Claude Code, Codex, BotSpot, current market data, and a paper broker working together.',
  },
  {
    title: 'A stock research workflow you can repeat',
    detail: 'Filings, financials, earnings calls, insider data, news, macro evidence, and sources you can check.',
  },
  {
    title: 'AI-powered market scanners',
    detail: 'TradingView, Pine Script, and scanners for named setups across stocks, options, crypto, and prediction markets.',
  },
  {
    title: 'A backtested trading strategy',
    detail: 'One repeatable setup turned into rules, tested honestly, and used for a paper-trading signal.',
  },
  {
    title: 'An always-on AI trading agent',
    detail: 'A scheduled BotSpot agent that wakes up, repeats the workflow, and paper trades inside your rules.',
  },
];

const SKILLS = [
  'Agentic trading', 'Claude Code', 'Codex', 'SEC filings', 'Fundamental analysis',
  'TradingView screeners', 'Pine Script', 'Technical analysis', 'Backtesting',
  'Stocks and ETFs', 'Options', 'Crypto', 'Prediction markets', 'Scheduled AI agents',
];

const INCLUDED = [
  {
    img: '/images/courses/inc-recordings.webp',
    name: 'Five live sessions with Rob',
    detail: 'Build live, ask questions, and bring your own setup for review.',
  },
  {
    img: '/images/courses/inc-recordings.webp',
    name: 'Recordings you keep',
    detail: 'Every session is added to your course portal for permanent access.',
  },
  {
    img: '/images/courses/inc-prompts.webp',
    name: 'Prompts and workflow pack',
    detail: 'The checklists, prompts, and working material used in class.',
  },
  {
    img: '/images/courses/inc-prompts.webp',
    name: COMPETITION.name,
    detail: COMPETITION.stackDetail,
  },
  {
    img: '/images/courses/inc-guarantee.webp',
    name: `${GUARANTEE_DAYS}-day guarantee`,
    detail: `Try the bootcamp and request a refund within ${GUARANTEE_DAYS} days if it is not for you.`,
  },
];

const REQUIREMENTS = [
  {
    title: 'A Mac, Windows, or Linux laptop',
    detail: 'No high-end machine is required.',
  },
  {
    title: 'About three hours a week',
    detail: 'One live session plus time to practise.',
  },
  {
    title: 'No coding experience',
    detail: 'You direct the tools in plain English. We set up paper trading together.',
  },
];

const FIT_ITEMS = [
  'You want to use AI for real research, scanning, trading, and automation.',
  'You want to place paper trades while you learn, not just watch lectures.',
  'You want a strategy that can become a scheduled, repeating AI agent.',
];

const FAQS = [
  {
    q: 'Do I need to know how to code?',
    a: 'No. You direct Claude Code and Codex in plain English, and we build the workspace together.',
  },
  {
    q: 'What is agentic trading?',
    a: 'It means giving AI a trading job it can repeat: research the market, scan for a setup, prepare or place a paper order, and run again on a schedule inside rules you control.',
  },
  {
    q: 'Will I trade real money in class?',
    a: 'You start with paper trading. The course teaches a reviewed workflow before live capital, and you decide if or when to move beyond paper.',
  },
  {
    q: 'Is this a course about building trading bots?',
    a: 'You start by using AI to research and place reviewed paper trades. Then you build and backtest a repeatable strategy and turn it into a scheduled AI-native agent in week five.',
  },
  {
    q: 'Which markets and asset classes does the bootcamp cover?',
    a: 'The examples cover stocks and ETFs, options, crypto and crypto futures with Bitunix, and prediction-market research with Polymarket. You can use the same research, scanning, and agent workflow across supported markets.',
  },
  {
    q: 'What happens in a live session?',
    a: 'Rob demonstrates the workflow, you build alongside him, and the group reviews real setups and questions.',
  },
  {
    q: 'What if I miss a session?',
    a: 'Every session is recorded and added to your portal, with permanent access.',
  },
  {
    q: 'What is the cohort trading competition?',
    a: `${COMPETITION.body} Participation is optional.`,
  },
  {
    q: 'What if it is not for me?',
    a: `Request a refund within ${GUARANTEE_DAYS} days. No forms and no argument.`,
  },
];

const REVIEW_SHOTS = [
  { src: '/images/reviews/tp-0.jpg', alt: 'Trustpilot review by Mauricio Rascon, five stars', tilt: -1.6 },
  { src: '/images/reviews/tp-3.jpg', alt: 'Trustpilot review by Worth, five stars', tilt: 1.2 },
  { src: '/images/reviews/tp-2.jpg', alt: 'Verified Trustpilot review by Daniel Ogola, five stars', tilt: -0.9 },
  { src: '/images/reviews/tp-6.jpg', alt: 'Trustpilot review by Charlie Murphy, five stars', tilt: 1.7 },
  { src: '/images/reviews/tp-4.jpg', alt: 'Trustpilot review by Robert Sanon, five stars', tilt: -1.3 },
  { src: '/images/reviews/tp-5.jpg', alt: 'Trustpilot review by Larry Ayala, five stars', tilt: 1.0 },
];

const CtaBand = ({ line, label, onEnroll, disabled }) => (
  <Reveal>
    <div className="cp-cta-band">
      <p>{line}</p>
      <button type="button" className="cp-btn-primary" data-testid="enroll-curriculum"
        onClick={onEnroll} disabled={disabled}>
        {label}
      </button>
    </div>
  </Reveal>
);

const CoursePage = ({ variantKey = 'control', experimentContext = null }) => {
  const { user } = useUser();
  const hero = HERO_VARIANTS[variantKey] || HERO_VARIANTS.control;
  const { startCheckout } = useCheckoutRedirect('course_sales');
  const {
    isLoading, plans, upfrontPlan, selfPaced, sessions, cohortStart,
    isEnrollable, formatSessionDate, promoEndsAt, promoName, hasActivePromo,
    pricingError,
  } = useCourseCatalog();

  const [selectedPlanKey, setSelectedPlanKey] = useState(null);
  useEffect(() => {
    if (!selectedPlanKey && upfrontPlan) setSelectedPlanKey(upfrontPlan.planKey);
  }, [upfrontPlan, selectedPlanKey]);

  const selectedPlan = plans.find((plan) => plan.planKey === selectedPlanKey) || upfrontPlan;

  const track = useCallback((event, extra = {}) => {
    pushEvent({
      event,
      source: window.location.pathname,
      variant: hero.key,
      userId: user?.id || user?.sub,
      email: user?.email,
      ...(experimentContext || {}),
      ...extra,
    });
  }, [experimentContext, hero.key, user]);

  const enroll = useCallback((ctaLocation) => {
    const planKey = selectedPlan?.planKey || 'bootcamp-upfront';
    track(ANALYTICS_EVENTS.BOOTCAMP_CHECKOUT_STARTED, {
      cta_location: ctaLocation,
      path: 'live',
      productSlug: 'ai-bootcamp',
      planKey,
    });
    return startCheckout({
      products: [{ productSlug: 'ai-bootcamp', planKey }],
      flowContext: 'course_sales',
      metadata: {
        origin: {
          flow: 'course_sales',
          ctaLocation,
          variant: hero.key,
          experimentName: experimentContext?.experiment_name,
          experimentVariant: experimentContext?.experiment_variant,
          returnUrl: '/ai-bot-builder-bootcamp/watch?welcome=1',
        },
      },
      contact: { email: user?.email || '', name: user?.name || '' },
    });
  }, [startCheckout, track, selectedPlan, hero.key, experimentContext, user]);

  const startLabel = useMemo(
    () => (isEnrollable && cohortStart ? formatSessionDate(cohortStart) : null),
    [isEnrollable, cohortStart, formatSessionDate]
  );

  const facts = useMemo(() => ([
    {
      value: '5', label: 'Live sessions with Rob', card: null,
      blurb: 'One session a week, taught live and recorded.',
    },
    {
      value: '3 hours', label: 'A week', card: '/images/courses/fact-card-hours.webp',
      blurb: 'The live session plus time to practise.',
    },
    {
      value: `${GUARANTEE_DAYS} days`, label: 'Money-back guarantee',
      card: '/images/courses/fact-card-guarantee.webp',
      blurb: `Request a refund within ${GUARANTEE_DAYS} days if it is not for you.`,
    },
  ]), []);

  const enrollmentUnavailable = isLoading || Boolean(pricingError) || !isEnrollable;
  const ctaLabel = enrollmentUnavailable ? 'Enrollment unavailable' : 'Enroll now';

  return (
    <div className="course-page" data-testid="course-page" data-variant={hero.key}>
      <ScrollProgress />
      <Helmet>
        <title>{`${hero.courseName} | BotSpot`}</title>
        <meta name="description" content={`${HERO_OUTCOME} ${HERO_SUB}`} />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: FAQS.map((faq) => ({
              '@type': 'Question',
              name: faq.q,
              acceptedAnswer: { '@type': 'Answer', text: faq.a },
            })),
          })}
        </script>
      </Helmet>

      {hasActivePromo && promoEndsAt && (
        <div className="cp-promobar" data-testid="promo-bar">
          <span>{promoName || 'Limited-time pricing'}</span>
          <PromoCountdown endsAt={promoEndsAt} label="Ends in" />
        </div>
      )}

      <header className="cp-hero">
        <div className="cp-hero-copy">
          <Reveal><p className="cp-eyebrow">Live five-week cohort</p></Reveal>
          <Reveal delay={60}><h1>{hero.courseName}</h1></Reveal>
          <Reveal delay={100}><p className="cp-hero-outcome" data-testid="hero-outcome">{HERO_OUTCOME}</p></Reveal>
          <Reveal delay={140}><p className="cp-sub">{HERO_SUB}</p></Reveal>
          <Reveal delay={180}><div className="cp-hero-rating"><StarRating rating={4.6} /></div></Reveal>

          <Reveal delay={220}>
            {pricingError ? (
              <p className="cp-price-error" data-testid="pricing-error" role="alert">
                Pricing is temporarily unavailable. Refresh or contact us for help.
              </p>
            ) : (
              <div className="cp-hero-price" data-testid="hero-price">
                {selectedPlan?.hasDiscount && selectedPlan.originalDisplay
                  ? <span className="cp-price-was">{selectedPlan.originalDisplay}</span>
                  : null}
                <span className="cp-price-now">{selectedPlan?.display || ''}</span>
                {selectedPlan?.installments > 1 ? <span className="cp-price-unit">/mo</span> : null}
                {selectedPlan?.savingsDisplay
                  ? <span className="cp-price-save">Save {selectedPlan.savingsDisplay}</span>
                  : null}
              </div>
            )}
          </Reveal>

          <Reveal delay={260}>
            <div className="cp-hero-cta">
              <button type="button" className="cp-btn-primary" data-testid="enroll-hero"
                onClick={() => enroll('hero')} disabled={enrollmentUnavailable}>
                {ctaLabel}
              </button>
              <a className="cp-btn-ghost" href={FIT_CALL_URL} target="_blank" rel="noopener noreferrer"
                onClick={() => track(ANALYTICS_EVENTS.BOOTCAMP_PATH_SELECTED, { path: 'fit_call', cta_location: 'hero' })}>
                Talk to Rob
              </a>
            </div>
          </Reveal>

          {startLabel && !enrollmentUnavailable
            ? <Reveal delay={290}><p className="cp-hero-when">Next cohort starts {startLabel}</p></Reveal>
            : null}

          {enrollmentUnavailable && !isLoading ? (
            <Reveal delay={300}>
              <p className="cp-enroll-fallback" data-testid="enroll-fallback">
                Live dates are not open yet.{' '}
                {selfPaced ? <><Link to={SELF_PACED_PATH}>Start self-paced for {selfPaced.display}</Link>{', or '}</> : null}
                <a href={FIT_CALL_URL} target="_blank" rel="noopener noreferrer">book a call with Rob</a>.
              </p>
            </Reveal>
          ) : null}

          <Reveal delay={320}>
            <div className="cp-hero-assurance">
              <p className="cp-firstnight">{FIRST_NIGHT_PROMISE}</p>
              <p className="ck-nocode">{NO_CODING_BADGE}</p>
            </div>
          </Reveal>

          {selfPaced && (
            <Reveal delay={340}>
              <p className="cp-selfpaced">
                Need flexible timing?{' '}
                <Link to={SELF_PACED_PATH}
                  onClick={() => track(ANALYTICS_EVENTS.BOOTCAMP_PATH_SELECTED, { path: 'self_paced' })}>
                  Self-paced from {selfPaced.display}
                </Link>
              </p>
            </Reveal>
          )}
        </div>

        <Reveal delay={160} className="cp-hero-art">
          <img src="/images/courses/ai-trading-workspace-hero.webp"
            alt="AI research, a price chart, and a controlled trade review in one workspace"
            width="1280" height="714" loading="eager" />
        </Reveal>
      </header>

      <LogoRow />
      <FactStrip facts={facts} />

      <section className="cp-section cp-problem">
        <Reveal><h2>AI can work faster. It still needs a process.</h2></Reveal>
        <div className="cp-problem-grid">
          {PAIN_POINTS.map((pain, index) => (
            <Reveal key={pain.title} delay={index * 80}>
              <img className="cp-problem-img" src={pain.img}
                alt={`${pain.title}. ${pain.body}`} loading="lazy" width="1280" height="714" />
            </Reveal>
          ))}
        </div>
        <Reveal delay={260}>
          <p className="cp-turn">Make the AI show its evidence, respect your limits, and leave every decision under your control.</p>
        </Reveal>
      </section>

      <section className="cp-section cp-learn">
        <Reveal><h2>What you will leave with</h2></Reveal>
        <div className="cp-deliverable-grid">
          {DELIVERABLES.map((item, index) => (
            <Reveal key={item.title} delay={index * 55}>
              <article className="cp-deliverable-card">
                <span aria-hidden="true">{index + 1}</span>
                <div><h3>{item.title}</h3><p>{item.detail}</p></div>
              </article>
            </Reveal>
          ))}
        </div>
        <Reveal delay={320}>
          <ul className="cp-skill-pills" aria-label="Course skills">
            {SKILLS.map((skill) => <li key={skill}>{skill}</li>)}
          </ul>
        </Reveal>
      </section>

      <PreviewVideo />

      <section className="cp-section cp-challenge-path" data-testid="challenge-path">
        <Reveal><h2>See Rob teach before you enroll</h2></Reveal>
        <Reveal delay={70}>
          <p>
            Join a free live challenge, build alongside Rob on Zoom, ask questions,
            complete the short assignments, and watch past challenge recordings.
          </p>
        </Reveal>
        <Reveal delay={120}>
          <Link className="cp-btn-ghost" to={CHALLENGES_PATH}>Explore the free challenges</Link>
        </Reveal>
      </section>

      <section className="cp-section cp-curriculum" id="curriculum" data-testid="curriculum">
        <Reveal><h2>The curriculum: five weeks, one working system</h2></Reveal>
        {sessions.length ? (
          <CurriculumAccordion sessions={sessions} formatDate={formatSessionDate} />
        ) : (
          <Reveal>
            <p className="cp-muted" data-testid="curriculum-unavailable" role="status">
              Live dates and session details will appear here when the next cohort opens.
            </p>
          </Reveal>
        )}
      </section>

      <CtaBand line="Build it live, then keep every recording."
        label={ctaLabel} onEnroll={() => enroll('after_curriculum')}
        disabled={enrollmentUnavailable} />

      <section className="cp-section cp-included">
        <Reveal><h2>How the live bootcamp works</h2></Reveal>
        <ul className="cp-included-list">
          {INCLUDED.map((item, index) => (
            <Reveal as="li" key={item.name} delay={index * 55}>
              <img className="cp-included-img" src={item.img} alt="" loading="lazy" width="512" height="512" />
              <div><h3>{item.name}</h3><p>{item.detail}</p></div>
            </Reveal>
          ))}
        </ul>
      </section>

      <section className="cp-section cp-instructor">
        <Reveal><h2>Learn live with Rob Grzesik</h2></Reveal>
        <Reveal delay={70}>
          <div className="cp-instructor-body">
            <img src="/images/rob.jpg" alt="Rob Grzesik" width="200" height="200" decoding="async" />
            <div>
              <p>
                Rob has 25+ years in software engineering and 15+ years in financial technology.
                He built trading infrastructure at Voyager Digital and created Lumibot, the open-source
                algorithmic trading framework used by developers worldwide.
              </p>
              <p>
                He has built more than 1,000 trading systems and teaches every session himself.
                The method comes from one lesson learned the expensive way: confidence is not evidence.
              </p>
            </div>
          </div>
        </Reveal>
      </section>

      <section className="cp-section cp-reviews">
        <Reveal>
          <div className="cp-reviews-head"><h2>What students say</h2><StarRating rating={4.6} compact /></div>
        </Reveal>
        <div className="cp-reviews-wall">
          {REVIEW_SHOTS.map((shot, index) => (
            <Reveal key={shot.src} delay={index * 60} className="cp-review-shot">
              <a href={TRUSTPILOT_URL} target="_blank" rel="noopener noreferrer"
                style={{ '--tilt': `${shot.tilt}deg` }}>
                <img src={shot.src} alt={shot.alt} loading="lazy" />
              </a>
            </Reveal>
          ))}
        </div>
        <Reveal delay={380}>
          <p className="cp-reviews-more"><a href={TRUSTPILOT_URL} target="_blank" rel="noopener noreferrer">Read all reviews on Trustpilot</a></p>
        </Reveal>
      </section>

      <section className="cp-section cp-fit cp-requirements" data-testid="fit-section">
        <Reveal><h2>Is this for you?</h2></Reveal>
        <div className="cp-fit-compact">
          <Reveal>
            <div className="cp-fit-col is-yes">
              <h3>A good fit if</h3>
              <ul>{FIT_ITEMS.map((item) => <li key={item}>{item}</li>)}</ul>
            </div>
          </Reveal>
          <Reveal delay={80}>
            <div className="cp-fit-col cp-need-col">
              <h3>What you need</h3>
              <ul>
                {REQUIREMENTS.map((item) => (
                  <li key={item.title}><strong>{item.title}</strong><span>{item.detail}</span></li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
        <Reveal delay={160}>
          <p className="cp-fit-foot">
            Want to check the fit? <a href={FIT_CALL_URL} target="_blank" rel="noopener noreferrer">Ask Rob directly.</a>
          </p>
        </Reveal>
      </section>

      <section className="cp-section cp-pricing" id="pricing" data-testid="pricing">
        <Reveal><h2>Choose how you pay</h2></Reveal>
        {isLoading ? (
          <p className="cp-muted">Loading current pricing...</p>
        ) : pricingError ? (
          <Reveal>
            <div className="cp-pricing-card" data-testid="pricing-error-card">
              <p className="cp-price-error" role="alert">Current pricing could not be loaded. Refresh or contact Rob.</p>
              <a className="cp-btn-primary" href={FIT_CALL_URL} target="_blank" rel="noopener noreferrer">Talk to Rob</a>
              <GuaranteeBlock compact />
            </div>
          </Reveal>
        ) : (
          <Reveal delay={70}>
            <div className="cp-pricing-card">
              {hasActivePromo && promoEndsAt ? (
                <div className="cp-pricing-promo">
                  <strong>{promoName || 'Limited-time pricing'}</strong>
                  <PromoCountdown endsAt={promoEndsAt} label="Ends in" />
                </div>
              ) : null}
              <PlanSelector plans={plans} selectedKey={selectedPlanKey} onSelect={setSelectedPlanKey} />
              <button type="button" className="cp-btn-primary cp-btn-block" data-testid="enroll-pricing"
                onClick={() => enroll('pricing')} disabled={enrollmentUnavailable}>{ctaLabel}</button>
              <p className="ck-nocode cp-nocode-cta">{NO_CODING_BADGE}</p>
              <GuaranteeBlock compact />
              <p className="cp-pricing-links">
                {selfPaced ? <Link to={SELF_PACED_PATH}>Self-paced from {selfPaced.display}</Link> : null}
                <a href={FIT_CALL_URL} target="_blank" rel="noopener noreferrer">Book a call with Rob</a>
              </p>
            </div>
          </Reveal>
        )}
      </section>

      <section className="cp-section cp-faq">
        <Reveal><h2>Questions people ask</h2></Reveal>
        <div className="cp-faq-list">
          {FAQS.map((faq) => (
            <Reveal key={faq.q}>
              <details><summary>{faq.q}</summary><p>{faq.a}</p></details>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="cp-section cp-final" style={{
        backgroundImage:
          'linear-gradient(rgba(6,11,22,0.72), rgba(6,11,22,0.88)), '
          + 'url("/images/courses/final-cta-bg.webp")',
      }}>
        <Reveal>
          <h2>Build an AI trading agent that keeps working</h2>
          <p>{startLabel ? `The next cohort starts ${startLabel}.` : 'Place a paper trade every week and finish with a scheduled agent.'}</p>
          <div className="cp-final-actions">
            <button type="button" className="cp-btn-primary" data-testid="enroll-final"
              onClick={() => enroll('final')} disabled={enrollmentUnavailable}>{ctaLabel}</button>
            <a className="cp-btn-ghost" href={FIT_CALL_URL} target="_blank" rel="noopener noreferrer">Talk to Rob</a>
          </div>
          <p className="ck-nocode cp-nocode-cta">{NO_CODING_BADGE}</p>
        </Reveal>
      </section>

      <div className="cp-sticky">
        <span className="cp-sticky-meta">
          <span className="cp-sticky-course">{hero.courseName}</span>
          {startLabel ? <span className="cp-sticky-when">Starts {startLabel}</span> : null}
        </span>
        <span>
          {pricingError || !selectedPlan ? 'Pricing unavailable' : (
            `${selectedPlan.display}${selectedPlan.installments > 1
              ? `/mo · ${selectedPlan.totalDisplay} total` : ''}`
          )}
        </span>
        <button type="button" data-testid="enroll-sticky"
          onClick={() => enroll('sticky')} disabled={enrollmentUnavailable}>{ctaLabel}</button>
      </div>

      <p className="cp-disclaimer">
        BotSpot is not an investment advisor. This course is educational and does not promise returns
        or recommend any security.
      </p>
    </div>
  );
};

export default CoursePage;
